import { activeHighlight } from './_activeHighlight.js'
import { headerOverlayPatch } from './headerOverlayState.js'

// NotificationBell — the header bell + unread badge, opening the
// notification center in the shell PageSlot (slot-first), with the shell-root
// dropdown layer as the slot-less consumers' fallback.
//
// CANONICAL copy (2026-07-31): the workspace package used to ship its own
// bell that shadowed this one in the shell (two divergent bells behind one
// key — the same collision family the panel had). This shared bell now does
// everything both did: unread badge from `root.notifs`, open-state
// highlight, and `el.call('openDropdown')` into DropdownRoot — so the panel
// renders in the top layer (viewport coords, glass theme, ESC +
// outside-click owned by the plugin) instead of an in-header absolute panel
// trapped in the header's stacking context.
//
// Dropdown-first (CORRECTED 2026-08-06 per Nika "notifications should open
// dropdown - not pageslot - only if expand is clicked ... opening in
// pageslot was meant to open each individual notification click - not the
// icon click"): the bell click opens the dropdown PANEL; the full /inbox
// notification center mounts in the shell PageSlot ONLY via the panel's
// expand/View-all affordance (NotificationPanel footer → openInSlot).
// Individual notification rows keep their in-place targets (ticket/event
// modals, slot-mounted feature routes — openNotificationTarget). Consumers
// without even the dropdown layer route to /inbox — the bell always does
// SOMETHING useful.
//
// `notificationsPanelOpen` stays the open-state mirror (state-app.js) for the
// dropdown path — written by the dropdown's onOpen/onClose so the bell's
// active ring reads one root flag, decoupled from the plugin's internal layer
// state. The slot path's open-state is `root._canvasSlot === '/inbox'`.

export const NotificationBell = {
  // FA204 / frank trap — unread/slotOpenOnInbox/toggleNotificationsPanel
  // used to be bare module-scope const/function read from inside
  // `active`/`class.isActive`/`hide`/`text`/`onMousedown`/`onClick`
  // factories; they read as undefined once this component is frank.toJSON'd
  // for platform delivery (workspace d6ea1142,
  // project_frank_drops_module_local_helpers). Moved into `scope`;
  // toggleNotificationsPanel calls its sibling via `this.slotOpenOnInbox`
  // (object-literal properties can't see each other as bare identifiers).
  scope: {
    unread(root) {
      return (root?.notifs || []).filter((n) => !n.read).length
    },
    slotOpenOnInbox(root) {
      return root?._canvasSlot === '/inbox'
    },
    toggleNotificationsPanel(e, el, s) {
      e.preventDefault()
      e.stopPropagation()
      // Icon press = the dropdown panel, ALWAYS (see the header note — the
      // PageSlot inbox is the panel's expand affordance, never the icon's).
      // A bell press while the slot inbox is open closes the slot first so
      // the two notification surfaces never stack.
      if (this.slotOpenOnInbox(s.root)) {
        s.root.update({ _canvasSlot: null, _canvasSlotSecond: null, _slotFullscreen: null })
        return
      }
      if (typeof el?.context?.functions?.openDropdown !== 'function') {
        el.router('/inbox', el.getRoot())
        return
      }
      if (s.root?.notificationsPanelOpen) {
        el.call('closeDropdown')
        return
      }
      // The bell's tooltip must not sit over the panel it just opened (live
      // 2026-09-26: "Notifications" stayed painted over the open popover).
      // Close a tooltip already on screen; `tooltipText` below answers
      // nothing while the panel is open, so a pending show stays dark too.
      el.call('closeTooltip', el.tooltipId || el.key)
      el.call('openDropdown', {
        // Under the bell on a top navbar; beside a left or right rail, from
        // the rail's outer edge (functions/navbarLayout.js navbarMenuAnchor —
        // under the bell it opened over the rail).
        ...el.call('navbarMenuAnchor', 'bottom-start'),
        id: 'notifications',
        // The popover recipe (DESIGN_SPEC §6.1): the shell glass on the
        // overlay elevation, the card radius, the panel's own width token.
        // The plugin Panel is chromeless by default since
        // FW-DROPDOWN-PANEL-STALE-SHELL-PAINT-1; theme + shadow + round opt
        // back in explicitly.
        // The panel's own width (NotificationPanel: overlayMd — the Georgian
        // filter needs it).
        minWidth: 'overlayMd',
        maxHeight: '72dvh',
        round: 'radiusCard',
        theme: 'canvas-card-glass',
        shadow: 'overlay',
        content: { extends: 'NotificationPanel' },
        onOpen: () => s.root.update(headerOverlayPatch('notifications', true)),
        onClose: () => s.root.update({ notificationsPanelOpen: false })
      })
    }
  },
  // The house tooltip, fed by `title` (TitleTooltipTrigger keeps the native one off).
  extends: ['IconButton', 'NavbarTooltipTrigger'],
  // INTERACT-1 item 4 (tickets/sonnet.md, WAVE-H interaction sweep,
  // 2026-08-14/15): live DOM inspection found this button — the other of
  // the two top-left icon-only nav buttons flagged alongside Messenger.js's
  // chat icon — rendering with NEITHER a `title` NOR an `aria-label`
  // attribute. `title` stays flat (unchanged); switched `aria: { label }`
  // to the bare `'aria-label'` kebab key. See Messenger.js's matching note
  // for the full reasoning — including why an `attr: { title }` wrapper was
  // tried and reverted (FA105 Frankability violation + a regression from
  // the framework's own confirmed-correct flat-promotion path).
  title: (el) => el.call('polyglot', 'shell.notificationBell.tooltip'),
  'aria-label': (el) => el.call('polyglot', 'shell.notificationBell.tooltip'),
  // The tooltip plugin reads this at SHOW time: nothing to show while the
  // panel is open, so the name never paints over the popover it labels.
  tooltipText: (el, s) =>
    s.root?.notificationsPanelOpen ? '' : el.call('polyglot', 'shell.notificationBell.tooltip'),
  // The trigger's popup semantics: a dialog, and whether it is open.
  ariaHaspopup: 'dialog',
  ariaExpanded: (el, s) => (s.root?.notificationsPanelOpen ? 'true' : 'false'),
  position: 'relative',

  // `notifs` is written by NotificationWrapper AFTER this bell has mounted,
  // so the badge must re-render on that write or it would show the boot-time
  // count forever. `_canvasSlot` drives the slot-path active ring.
  subscribeTo: ['notifs', 'notificationsPanelOpen', '_canvasSlot'],

  active: (el, s) => !!s.root?.notificationsPanelOpen || el.scope.slotOpenOnInbox(s.root),
  ...activeHighlight,
  class: {
    isActive: (el, s) => !!s.root?.notificationsPanelOpen || el.scope.slotOpenOnInbox(s.root)
  },

  // `name` (not `icon`) — required, not stylistic. See Messenger.js's Icon
  // comment for the full mechanism: IconText.Icon.if never reads the
  // child's own `el.icon`, only `el.name` (or `parent.icon`). This bell
  // rendered empty after smbls `2e4ba032e` removed the SquareButton
  // `icon:'smile'` placeholder that had been masking the wrong key
  // (tickets/opus.md P1, 2026-08-09).
  Icon: { name: 'bell', boxSize: 'A A' },

  // Crisp alarm pill, floated just off the bell's corner. `hide:`, not
  // `if:` — v3.14's `if` is a MOUNT-TIME gate that never re-creates the
  // element: mounted at zero it would stay gone no matter how many
  // notifications arrived (the exact transition a badge exists to show).
  UnreadBadge: {
    hide: (el, s) => el.scope.unread(s.root) === 0,
    position: 'absolute',
    top: '-X',
    right: '-X',
    minWidth: 'A1',
    height: 'A1',
    padding: '0 W',
    round: 'radiusPill',
    // Fixed alert-badge pairing, deliberately NOT theme-adaptive — an alert
    // reads red on both shells. But brand `red` (#EB6650) is tuned as a PLATE
    // colour, and white on it measures 3.21:1: fine for a dot (3:1 non-text
    // floor) and a fail for a COUNT, which is text. `redDeep` is the same
    // alert red deepened — white on #A83520 = 6.53:1. Measured across every
    // route by the aa-contrast sweep, 2026-08-09; 15 violation groups traced
    // to this one badge shape.
    background: 'redDeep',
    color: 'white',
    fontSize: 'Y2',
    fontWeight: '700',
    lineHeight: '1',
    align: 'center center',
    pointerEvents: 'none',
    text: (el, s) => {
      const count = el.scope.unread(s.root)
      return count > 99 ? '99+' : String(count)
    }
  },

  // Pointer path = onMousedown (fires on the ORIGINAL target before a
  // reactive re-render can swap the node out from under the gesture — the
  // header's documented dead-click window, most frequent while a PageSlot's
  // load churn re-renders the chrome); keyboard path = onClick with
  // `detail === 0` (Enter/Space synthesize detail-0 clicks). The guard keeps
  // a single pointer gesture from firing the TOGGLE twice (mousedown +
  // click = open-then-close = "the bell doesn't open"). Same split
  // HeaderTabs' _openTab/SearchBtn established.
  onMousedown: (e, el, s) => {
    if (e.button !== 0) return
    el.scope.toggleNotificationsPanel(e, el, s)
  },
  onClick: (e, el, s) => {
    if (e.detail !== 0) {
      e.preventDefault()
      e.stopPropagation()
      return
    }
    el.scope.toggleNotificationsPanel(e, el, s)
  }
}
